import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatButton } from "@angular/material/button";
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltip } from "@angular/material/tooltip";
import moment from 'moment';
import { Subject } from 'rxjs';
import { DeleteModalComponent } from "../delete-modal/delete-modal.component";

type SanitizerKind = 'alias' | 'name';

@Component({
  selector: 'app-modal',
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatSelectModule,
    CommonModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatSliderModule,
    MatButton,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltip,
    DeleteModalComponent
],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
  ]
})
export class ModalComponent implements OnInit, OnDestroy {
  action: 'add' | 'edit' | 'delete' | undefined;
  formData!: FormGroup;
  visibleKeys: string[] = [];
  readonlyFields: { edit?: string[], add?: string[], delete?: string[] } = {};
  hiddenKeys: string[] = [];
  title: string = '';
  labels: Record<string, string> = {};
  isLoading = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<ModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      modalType?: 'suggestions' | 'users' | '',
      action: 'add' | 'edit' | 'delete',
      payload: any,
      visibleFields?: string[],
      readonlyFields?: { edit?: string[], add?: string[], delete?: string[] },
      hiddenFields?: string[],
      hiddenFieldsValues?: Record<string, any>,
      title?: string,
      labels?: Record<string, string>,
      fieldTypes?: Record<string, 'input' | 'select' | 'date' | 'checkbox' | 'slider'>,
      selectOptions?: Record<string, { value: string; label: string }[]>,
      fieldValidators?: Record<string, ValidatorFn[]>,
      sanitizers?: Record<string, SanitizerKind>,
      nonRequiredFields?: { edit?: string[], add?: string[], delete?: string[] },
      loadingSelect?: Record<string, boolean>,
      errorSelect?: Record<string, boolean>
    },
    private readonly fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.action = this.data.action;
    this.visibleKeys = this.data.visibleFields || Object.keys(this.data.payload || {});
    this.hiddenKeys = this.data.hiddenFields || [];
    this.title = this.data.title || this.getTitle();
    this.labels = this.getLabels();
    const hiddenFieldsValues = this.data.hiddenFieldsValues || {};

    const controls: { [key: string]: FormControl | FormArray | FormGroup } = {};
    const mode = this.data.action;
    const readonlyInMode = this.data.readonlyFields?.[mode] || [];
    const nonRequiredInMode = this.data.nonRequiredFields?.[mode] || [];

    // Campi visibili
    this.visibleKeys.forEach(key => {
      const isReadonly = readonlyInMode.includes(key);
      const isLoading = this.data.loadingSelect?.[key] ?? false;
      const isDisabled = isReadonly || isLoading;
      const type = this.data.fieldTypes?.[key] ?? 'input';
      let initialValueVisibleField: any = this.data.payload[key];
      
      if (type === 'date') {
        initialValueVisibleField = this.fromIsoDateOnly(initialValueVisibleField);
      }

      if (initialValueVisibleField === undefined) {
        if (type === 'slider') {
          initialValueVisibleField = 0;
        } else if (type === 'checkbox') {
          if(this.data.modalType === 'users'){
            initialValueVisibleField = true;
          } else {
            initialValueVisibleField = false;
          }
        } else {
          initialValueVisibleField = '';
        }
      }

      const extraValidators = this.data.fieldValidators?.[key] ?? [];

      // validazione required di default con eccezioni se presenti
      const validators = [
        ...(nonRequiredInMode.includes(key) ? [] : [Validators.required]),
        ...extraValidators
      ];
      
        controls[key] = this.fb.control(
          { value: initialValueVisibleField, disabled: isDisabled },
          validators
        );
      
    });

    // Campi nascosti
    this.hiddenKeys.forEach(key => {
      const hiddenFieldValue = this.data.payload?.[key] ?? hiddenFieldsValues[key] ?? '';
      controls[key] = this.fb.control(hiddenFieldValue);
    });


    this.formData = this.fb.group(controls);

  }

  getTitle(): string {
    if(this.action === 'add' && this.data.modalType === 'suggestions'){
      return 'Escludi';
    } else {
      switch (this.action) {
        case 'add': return 'Aggiungi';
        case 'edit': return 'Modifica';
        case 'delete': return 'Elimina';
        default: return '';
      }
    }
  }

  getLabels(): Record<string, string> {
    return this.data.labels || {};
  }

  onSliderNumberInput(key: string, event: Event) {
    const el = event.target as HTMLInputElement | null;
    if (!el) return;

    let nextValue: number;
    if (el.value === '') {
      nextValue = 0;
    } else if (Number.isNaN(el.valueAsNumber)) {
      nextValue = Number(el.value);
    } else {
      nextValue = el.valueAsNumber;
    }

    this.formData.get(key)?.setValue(nextValue, { emitEvent: true });
  }

  private toIsoDateOnly(date: moment.Moment | null): string | null {
    if (!date) return null;
   
    return date.format('YYYY-MM-DD');
  }

  private fromIsoDateOnly(value: string | null): moment.Moment | null {
    if (!value) return null;
    
    return moment(value, 'YYYY-MM-DD');
  }

  onModalClose(): void {
    this.dialogRef.close();
  }

  onModalSave(): void {
    console.log('formdata', this.formData.value);
    if (this.formData.invalid && this.action !== 'delete') {
      return;
    }
    let dataToPass: any;
    const readOnlyFields = this.data.readonlyFields?.edit;

    if (this.action === 'delete') {
      dataToPass = this.data.payload;
    } else if (this.action === 'edit') {
      if (this.data.payload.id) {
        dataToPass = { ...this.formData.value, id: this.data.payload.id };
      } else {
        dataToPass = { ...this.formData.value };
      }

      if (readOnlyFields && readOnlyFields?.length > 0) {
        readOnlyFields.forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(this.data.payload, key)) {
            dataToPass[key] = this.data.payload[key];
          }
        });
      }
    } else if (this.action === 'add') {
      dataToPass = this.formData.value;
    }

    if(this.action !== 'delete'){
      // normalizzo le date
      Object.entries(this.data.fieldTypes || {}).forEach(([key, type]) => {
        if (type === 'date') {
          dataToPass[key] = this.toIsoDateOnly(dataToPass[key]);
        }
      });
    }
    
    console.log('data to pass', dataToPass);
    this.dialogRef.close(dataToPass);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}